import { ExternalLink } from 'lucide-react'

import { SplitsLink } from '~/components/Link'
import { LabelValue, NetworkDisplay } from '~/components/component-library'
import { CostDisplay } from '~/components/txn-display/cost-display/CostDisplay'
import { SponsoredFeeAmount } from '~/components/txn-display/cost-display/SponsoredFeeAmount'
import { useTransactionCost } from '~/hooks/transactions/useTransactionCost'
import {
  OrgMerkleTransaction,
  OrgRegularTransaction,
  PublicMerkleTransaction,
} from '~/types'
import { getExplorerTransactionLinkChainId } from '~/utils/addresses'
import { cn } from '~/utils/tailwind-utils'

export const MerkleTransactionCostDisplay = ({
  transaction,
}: {
  transaction: OrgMerkleTransaction | PublicMerkleTransaction
}) => {
  const transactionCost = useTransactionCost({
    isMerkleTransaction: true,
    childTransactions: transaction.childTransactions as OrgRegularTransaction[],
  })

  // add up the transaction costs for all non-sponsored child transactions
  // Each chain can only have one child transaction, so we can safely sum by chain
  const nonSponsoredTransactionCost =
    transaction.childTransactions.reduce((acc, childTransaction) => {
      if (childTransaction.isSponsored) return acc
      return (
        acc +
        (transactionCost.transactionCostByChain?.[childTransaction.chainId] ??
          0)
      )
    }, 0) ?? 0

  const totalTransactionCost = transactionCost.transactionCost ?? 0

  // check if all child transactions are sponsored
  const areAllChildTransactionsSponsored = transaction.childTransactions.every(
    (childTransaction) => childTransaction.isSponsored,
  )

  return (
    <CostDisplay
      isCollapsible
      defaultOpen={false}
      isLoading={transactionCost.isLoading}
      isSponsored={areAllChildTransactionsSponsored}
      cost={
        areAllChildTransactionsSponsored
          ? totalTransactionCost
          : nonSponsoredTransactionCost
      }
    >
      <div
        className={cn(
          'relative flex flex-col gap-2.5 rounded-lg bg-white px-3.5 py-3 text-sm text-secondary ring-1 ring-inset ring-gray-200 dark:bg-gray-900 dark:ring-gray-700',
        )}
      >
        {transaction.childTransactions.map((childTransaction, index) => {
          const explorerLink = getExplorerTransactionLinkChainId(
            childTransaction.transactionHash,
            childTransaction.chainId,
          )

          const chainCost =
            transactionCost.transactionCostByChain?.[
              childTransaction.chainId
            ] ?? 0
          const feeBadge = (
            <SponsoredFeeAmount
              cost={chainCost}
              isSponsored={childTransaction.isSponsored}
              hideBadgeText
            />
          )

          return (
            <LabelValue
              key={index}
              label={
                <NetworkDisplay
                  layout="standard"
                  size="small"
                  chainId={childTransaction.chainId}
                />
              }
              size="small"
              value={
                explorerLink ? (
                  <SplitsLink
                    className="flex flex-shrink-0 items-center gap-2"
                    path={explorerLink}
                    target="tab"
                  >
                    {feeBadge}
                    {explorerLink && <ExternalLink className="size-3.5" />}
                  </SplitsLink>
                ) : (
                  <span className="flex flex-shrink-0 items-center gap-2">
                    {feeBadge}
                  </span>
                )
              }
            />
          )
        })}
      </div>
    </CostDisplay>
  )
}
